import { toRaw } from 'vue';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  IAssignmentsResponse,
  IBodyUser,
  IBodyUserCustomer,
  IBodyUserInfo,
  IBodyUserUpdatePassword,
  ICustomersResponse,
  ICustomerUserResponse,
  IRemovalsResponse,
  IRoleBody,
  IStaffResponse,
  IStaffSingleResponse,
  IUpdateStaffRole,
  IUserPaginatedResponse,
} from '@/types/user.type';
import {
  qStaff,
  sortBy,
  sortDir,
  statusStaff,
  user_customer_id,
  user_customer_page,
  user_email_page,
  user_page,
  user_phone_page,
  user_role_page,
  user_staff_id,
  user_staff_page,
  userId,
} from '@/refs/user.refs';
import { restaurantId, token, userCommerceId } from '@/refs/common.refs';
import type { IFileImage } from '@/types/file.types';
import axios from 'axios';
import { VITE_API_URL } from '@/utils/runtimeConfig';

export const UserService = {
  /**
   * Crée un nouveau client (customer) dans le système.
   *
   * @async
   * @function createUserCustomer
   * @param {IBodyUserCustomer} data - Données du client à enregistrer.
   * @returns {Promise<any>} Réponse de l'API après la création du client.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  createUserCustomer: async (data: IBodyUserCustomer) => {
    try {
      const response = axiosInstance.post(`customers/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des clients (customers) avec pagination.
   *
   * @async
   * @function getUserCustomer
   * @returns {Promise<ICustomersResponse>} Données paginées des clients retournées par l'API.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  getUserCustomer: async () => {
    try {
      const page = Number(toRaw(user_customer_page.value));
      const response = await axiosInstance.get<ICustomersResponse>(
        `customers/search?pagination=true&page=${page}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère un client spécifique par son identifiant.
   *
   * @async
   * @function getUserCustomerById
   * @returns {Promise<IApiResponseCategory | undefined>}
   *          Données du client retournées par l'API, ou `undefined` si `user_customer_id` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  getUserCustomerById: async () => {
    try {
      if (user_customer_id.value === null) return;
      const id = toRaw(user_customer_id.value);
      const response = await axiosInstance.get<ICustomerUserResponse>(
        `customers/get/${id}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour un client existant par son identifiant.
   *
   * @async
   * @function updateUserCustomer
   * @param {Partial<IBodyUserCustomer>} userCustomerData - Données partielles du client à mettre à jour.
   * @returns {Promise<any | undefined>} Réponse de l'API après mise à jour, ou `undefined` si `user_customer_id` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  updateUserCustomer: async (userCustomerData: Partial<IBodyUserCustomer>) => {
    try {
      if (user_customer_id.value === null) return;
      const id = toRaw(user_customer_id.value);
      const response = await axiosInstance.put<any>(
        `customers/update/${id}`,
        userCustomerData,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un client existant par son identifiant.
   *
   * @async
   * @function DeleteUserCustomer
   * @returns {Promise<any | undefined>} Réponse de l'API après suppression, ou `undefined` si `user_customer_id` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  DeleteUserCustomer: async () => {
    try {
      if (user_customer_id.value === null) return;
      const id = toRaw(user_customer_id.value);
      const response = axiosInstance.delete(`customers/delete/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Crée un nouveau role (staff) dans le système.
   *
   * @async
   * @function createUserRoleStaff
   * @param { IRoleBody} data - Données du staff à enregistrer.
   * @returns {Promise<any>} Réponse de l'API après la création du client.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  createUserRoleStaff: async (data: IRoleBody) => {
    try {
      const response = axiosInstance.post(`auth/role/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des users (staffs roles) avec pagination.
   *
   * @async
   * @function getUserStaffRole
   * @returns {Promise<IStaffResponse>} Données paginées des clients retournées par l'API.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  getUserStaffRole: async () => {
    try {
      const page = Number(toRaw(user_staff_page.value));

      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());
      params.append('status', statusStaff.value);
      params.append('q', qStaff.value);
      // params.append("restaurantId", restaurantId.value);
      params.append('sortBy', sortBy.value);
      params.append('sortDir', sortDir.value);

      const response = await axiosInstance.get<IStaffResponse>(
        `roles/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère un staff role spécifique par son identifiant.
   *
   * @async
   * @function  getUserStaffRoleById
   * @returns {Promise<IStaffSingleResponse | undefined>}
   *          Données du client retournées par l'API, ou `undefined` si `user_customer_id` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  getUserStaffRoleById: async () => {
    try {
      if (user_staff_id.value === null) return;
      const id = toRaw(user_staff_id.value);
      const response = await axiosInstance.get<IStaffSingleResponse>(
        `role/get/${id}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour un role du staff existant par son identifiant.
   *
   * @async
   * @function updateUserStaffRole
   * @param {Partial<IUpdateStaffRole>} userStaffRoleData - Données partielles du stff role à mettre à jour.
   * @returns {Promise<any | undefined>} Réponse de l'API après mise à jour, ou `undefined` si `user_customer_id` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  updateUserStaffRole: async (userCustomerData: Partial<IUpdateStaffRole>) => {
    try {
      if (user_staff_id.value === null) return;
      const id = toRaw(user_staff_id.value);
      const response = await axiosInstance.put<any>(
        `auth/role/update/${id}`,
        userCustomerData,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un client existant par son identifiant.
   *
   * @async
   * @function DeleteUserStaffRole
   * @returns {Promise<any | undefined>} Réponse de l'API après suppression, ou `undefined` si `user_customer_id` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  DeleteUserStaffRole: async () => {
    try {
      if (user_staff_id.value === null) return;
      const id = toRaw(user_staff_id.value);
      const response = axiosInstance.delete(`auth/role/delete/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste complète des permissions disponibles dans le système.
   *
   * Cette fonction interroge l’API afin d’obtenir toutes les permissions
   * associées aux rôles utilisateurs. Elle est généralement utilisée
   * pour l’affichage, la gestion ou l’attribution des permissions
   * dans les interfaces d’administration.
   *
   * @async
   * @function getAllAvailablePermission
   * @returns {Promise<AxiosResponse<any>>} Réponse de l’API contenant la liste des permissions.
   * @throws {AxiosError} Rejette une erreur Axios si la requête HTTP échoue.
   */
  getAllAvailablePermission: async () => {
    try {
      const response = await axiosInstance.get<any>(`roles/permissions`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Crée un nouvel utilisateur (staff) dans le système.
   *
   * Cette fonction envoie les données d’un staff à l’API pour
   * l’enregistrement. Les données doivent respecter la structure
   * définie par l’interface `IBodyUser`.
   *
   * @async
   * @function createUser
   * @param {IBodyUser} data - Objet contenant les informations du staff à créer.
   * @returns {Promise<any>} Promesse résolue avec la réponse de l’API après la création du staff.
   *
   * @throws {AxiosError} Erreur renvoyée si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  createUser: async (data: IBodyUser) => {
    try {
      const response = await axiosInstance.post(`auth/signup.staff`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des utilisateurs (staffs) avec gestion
   * de la pagination et des filtres de recherche.
   *
   * Les résultats peuvent être filtrés par :
   * - rôle
   * - email
   * - numéro de téléphone
   *
   * La pagination est activée via les paramètres de requête.
   *
   * @async
   * @function getUser
   * @returns {Promise<IUserPaginatedResponse>} Réponse de l’API contenant
   * la liste paginée des utilisateurs et les métadonnées associées.
   *
   * @throws {AxiosError} Erreur renvoyée si la requête HTTP échoue
   * ou si l’API retourne une réponse invalide.
   */
  getUser: async () => {
    try {
      const page = Number(toRaw(user_page.value));

      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());

      console.log(userCommerceId.value);

      if (
        userCommerceId.value !== null &&
        userCommerceId.value !== 'null' &&
        userCommerceId.value !== undefined
      ) {
        params.append('restaurantId', userCommerceId.value.toString());
      }

      const response = await axiosInstance.get<IUserPaginatedResponse>(
        `auth/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère les rôles d'un utilisateur spécifique par son identifiant.
   *
   * Cette fonction effectue une requête HTTP GET vers l'API pour récupérer
   * les rôles associés à un utilisateur identifié par `userId.value`.
   * Si `userId.value` est `null`, la fonction termine immédiatement et retourne `undefined`.
   *
   * @async
   * @function getUserRolesById
   * @returns {Promise<AxiosResponse<any>> | undefined}
   *  Promesse résolue avec les données de l'utilisateur retournées par l'API,
   *  ou `undefined` si `userId.value` est `null`.
   * @throws {AxiosError} Renvoie une erreur si la requête HTTP échoue.
   */
  getUserRolesById: async () => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);
      const response = await axiosInstance.get<any>(`auth/user.role/${id}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
  /**
   * Met à jour les informations d’un utilisateur (staff) existant,
   * y compris le mot de passe si fourni.
   *
   * Cette fonction envoie les données de mise à jour partielles à l’API
   * pour modifier les informations d’un staff identifié par `userId.value`.
   * Si `userId.value` est `null`, la fonction retourne `undefined` sans effectuer de requête.
   *
   * @async
   * @function updateMyInfo
   * @param {Partial<IBodyUserInfo>} userData - Données partielles pour la mise à jour
   *   des informations du staff (ex. mot de passe, prénom, nom, téléphone, etc.).
   * @returns {Promise<AxiosResponse<any>> | undefined}
   *   Promesse résolue avec la réponse de l’API après mise à jour,
   *   ou `undefined` si `userId.value` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  updateMyInfo: async (userData: Partial<IBodyUserInfo>) => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);

      const response = await axiosInstance.put<any>(
        `auth/update.info`,
        userData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour les informations d’un utilisateur (staff) existant,
   * y compris le mot de passe si fourni.
   *
   * Cette fonction envoie les données de mise à jour partielles à l’API
   * pour modifier les informations d’un staff identifié par `userId.value`.
   * Si `userId.value` est `null`, la fonction retourne `undefined` sans effectuer de requête.
   *
   * @async
   * @function updateUserInfo
   * @param {Partial<IBodyUserInfo>} userData - Données partielles pour la mise à jour
   *   des informations du staff (ex. mot de passe, prénom, nom, téléphone, etc.).
   * @returns {Promise<AxiosResponse<any>> | undefined}
   *   Promesse résolue avec la réponse de l’API après mise à jour,
   *   ou `undefined` si `userId.value` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  updateUserInfo: async (userData: Partial<IBodyUserInfo>) => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);

      const response = await axiosInstance.put<any>(
        `auth/update.info/${id}`,
        userData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour le mot de passe d’un utilisateur (staff) existant.
   *
   * Cette fonction envoie les données de mise à jour à l’API pour
   * modifier le mot de passe d’un staff identifié par son `userId`.
   * Si `userId` est `null`, la fonction retourne `undefined`.
   *
   * @async
   * @function updateUserPassword
   * @param {Partial<IBodyUserUpdatePassword>} userData - Données partielles pour la mise à jour du mot de passe du staff.
   * @returns {Promise<any | undefined>} Promesse résolue avec la réponse de l’API après mise à jour,
   * ou `undefined` si `userId` est null.
   *
   * @throws {AxiosError} Erreur renvoyée si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  updateUserPassword: async (userData: Partial<IBodyUserUpdatePassword>) => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);

      const response = await axiosInstance.put<any>(
        `auth/update.password/${id}`,
        userData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour le mot de passe d’un utilisateur (staff) existant.
   *
   * Cette fonction envoie les données de mise à jour à l’API pour
   * modifier le mot de passe d’un staff identifié par son `userId`.
   * Si `userId` est `null`, la fonction retourne `undefined`.
   *
   * @async
   * @function updateMyPassword
   * @param {Partial<IBodyUserUpdatePassword>} userData - Données partielles pour la mise à jour du mot de passe du staff.
   * @returns {Promise<any | undefined>} Promesse résolue avec la réponse de l’API après mise à jour,
   * ou `undefined` si `userId` est null.
   *
   * @throws {AxiosError} Erreur renvoyée si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  updateMyPassword: async (userData: Partial<IBodyUserUpdatePassword>) => {
    try {
      const response = await axiosInstance.put<any>(
        `auth/update.password`,
        userData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour la photo de profil d’un utilisateur (staff) existant.
   *
   * Cette fonction envoie les données de l’image à l’API pour
   * modifier la photo d’un staff identifié par son `userId`.
   * Si `userId` est `null`, la fonction retourne `undefined`.
   *
   * @async
   * @function updateUserPhoto
   * @param {Partial<IFileImage>} userData - Données partielles contenant la photo à mettre à jour.
   * @returns {Promise<any | undefined>} Promesse résolue avec la réponse de l’API après mise à jour,
   * ou `undefined` si `userId` est null.
   *
   * @throws {AxiosError} Erreur renvoyée si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  updateUserPhoto: async (data: Partial<IFileImage>) => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('designation', data.designation);
      formData.append('restaurantId', data.restaurantId);

      const accessToken = token.value?.accessToken;
      if (userId.value === null) return;
      const id = toRaw(userId.value);
      const response = await axios.put(
        `${VITE_API_URL}auth/update.photo/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour la photo de profil de l’utilisateur courant (staff).
   *
   * Cette fonction envoie les données de la nouvelle photo à l’API
   * via un `FormData`, incluant le fichier, la désignation et l’ID
   * du restaurant associé. L’utilisateur doit être authentifié,
   * et le `userId` doit être défini.
   *
   * @async
   * @function updateMyPhoto
   * @param {Partial<IFileImage>} data - Données partielles de l’image à mettre à jour :
   *  - `file`: le fichier image à télécharger.
   *  - `designation`: nom ou description de l’image.
   *  - `restaurantId`: identifiant du restaurant associé (optionnel).
   * @returns {Promise<any | undefined>} Promesse résolue avec la réponse de l’API après mise à jour,
   * ou `undefined` si `userId` est null.
   *
   * @throws {AxiosError} Erreur renvoyée si la requête HTTP échoue ou si l’API retourne une réponse invalide.
   */
  updateMyPhoto: async (data: Partial<IFileImage>) => {
    try {
      const formData = new FormData();
      if (data.file) formData.append('file', data.file);
      if (data.designation) formData.append('designation', data.designation);
      if (data.restaurantId) formData.append('restaurantId', data.restaurantId);

      const accessToken = token.value?.accessToken;
      const response = await axios.put(
        `${VITE_API_URL}auth/update.photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
  /**
   * Supprime les rôles associés à un utilisateur existant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP PUT vers l'API pour retirer
   * les rôles d'un utilisateur spécifié. Si `userId.value` est `null`,
   * la fonction termine immédiatement sans effectuer de requête.
   *
   * @async
   * @function removeUserRoles
   * @param {IRemovalsResponse} data - Données contenant les rôles à supprimer.
   * @returns {Promise<AxiosResponse<any>> | undefined}
   *  Promesse résolue avec la réponse de l'API après suppression des rôles,
   *  ou `undefined` si `userId.value` est `null`.
   * @throws {AxiosError} Renvoie une erreur si la requête HTTP échoue.
   */
  removeUserRoles: async (data: IRemovalsResponse) => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);
      const response = await axiosInstance.put(`auth/role/delete/${id}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Assigne des rôles à un utilisateur existant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP PUT vers l'API pour ajouter
   * les rôles spécifiés à l'utilisateur identifié par `userId.value`.
   * Si `userId.value` est `null`, la fonction termine immédiatement et retourne `undefined`.
   *
   * @async
   * @function assignUserRoles
   * @param {IAssignmentsResponse} data - Données contenant le rôle à assigner et le restaurant associé.
   * @returns {Promise<AxiosResponse<IAssignmentsResponse>> | undefined}
   *  Promesse résolue avec la réponse de l'API après l'assignation du rôle,
   *  ou `undefined` si `userId.value` est `null`.
   * @throws {AxiosError} Renvoie une erreur si la requête HTTP échoue.
   */
  assignUserRoles: async (data: IAssignmentsResponse) => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);
      const response = await axiosInstance.put<any>(
        `auth/add.role/${id}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un client existant par son identifiant.
   *
   * @async
   * @function deleteUser
   * @returns {Promise<any | undefined>} Réponse de l'API après suppression, ou `undefined` si `userId` est null.
   * @throws {AxiosError} Renvoie une erreur si la requête échoue.
   */
  deleteUser: async () => {
    try {
      if (userId.value === null) return;
      const id = toRaw(userId.value);
      const response = axiosInstance.delete(`auth/delete/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  searchRolesGestion: async () => {
    try {
      const params = new URLSearchParams({
        pagination: 'true',
        status: 'all',
        q: '',
        sortBy: 'code',
        sortDir: 'ASC',
      });
      const response = await axiosInstance.get<IStaffResponse>(
        `roles/search?${params.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  searchUsersGestion: async (params: {
    role?: string | string[];
    q?: string;
    restaurantId?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: 'ASC' | 'DESC';
  }) => {
    try {
      const qs = new URLSearchParams();
      qs.append('pagination', 'true');
      qs.append('sortBy', 'created');
      qs.append('sortOrder', params.sortOrder ?? 'DESC');
      if (params.page) qs.append('page', String(params.page));
      if (params.pageSize) qs.append('pageSize', String(params.pageSize));
      if (params.role) {
        for (const r of Array.isArray(params.role)
          ? params.role
          : [params.role]) {
          qs.append('role', r);
        }
      }
      if (params.q) qs.append('email', params.q);
      if (params.restaurantId) qs.append('restaurantId', params.restaurantId);
      const response = await axiosInstance.get<IUserPaginatedResponse>(
        `auth/search?${qs.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  assignUserRolesById: async (
    targetUserId: string,
    assignments: {
      roleId: string;
      restaurantId?: string;
      organizationId?: string;
    }[],
  ) => {
    try {
      const response = await axiosInstance.put<any>(
        `auth/add.role/${targetUserId}`,
        { assignments },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  updateUserPhotoById: async (
    targetUserId: string,
    file: File,
    designation: string,
    restaurantId: string,
  ) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // formData.append('designation', designation);
      // if (restaurantId) formData.append('restaurantId', restaurantId);
      const accessToken = token.value?.accessToken;
      const response = await axios.put(
        `${VITE_API_URL}auth/update.photo/${targetUserId}`,
        formData,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  updateUserPasswordById: async (targetUserId: string, secret: string) => {
    try {
      const response = await axiosInstance.put<any>(
        `auth/update.password/${targetUserId}`,
        { secret },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
